import React from 'react';
import * as Sentry from '@sentry/react';
import Analytics from 'utils/Analytics';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Get current survey context
    const surveyContext = {
      survey_name: surveyInstance?.title,
      current_page: surveyInstance?.currentPageNo,
      current_question: surveyInstance?.currentPage?.questions?.[0]?.name,
      question_title: surveyInstance?.currentPage?.questions?.[0]?.title,
    };

    // Report to Sentry with properly typed extra info
    Sentry.captureException(error, {
      contexts: {
        survey: surveyContext,
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Track in Analytics
    Analytics.track('Survey Error', {
      error_message: error.message,
      ...surveyContext,
    });
  }

  handleGoBack = () => {
    if (surveyInstance?.currentPageNo > 0) {
      // Go back one page
      surveyInstance.prevPage();
      // Reset error state
      this.setState({
        hasError: false,
        error: null,
      });
    } else {
      // If we can't go back, reload the page
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorContainer}>
          <h2>Oops! Something went wrong.</h2>
          <p>Don't worry - your progress has been saved.</p>
          <div style={{display: 'flex', justifyContent: 'center'}}>
            <ButtonSolid title="Go Back" onClick={this.handleGoBack} />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
